import { FormProvider, useForm } from 'react-hook-form';
import { useDataProvider, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../../../api/adminDataProvider';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { Loader2, Send } from 'lucide-react';
import { useId } from 'react';

interface FormValues {
  text: string;
}

export function ChatMessageForm({
  unitId,
  insightId,
  chatId,
  onMessageSent,
  onReplyStarted,
  onReplyChunk,
  onReplyComplete,
  onReplyError,
}: {
  unitId: string | number;
  insightId: string | number;
  chatId: number | undefined;
  onMessageSent: (message: FormValues) => void;
  onReplyStarted: (chatId: number) => void;
  onReplyChunk: (chunk: string) => void;
  onReplyComplete: (context: {
    userMessageId: number;
    assistantMessageId: number;
  }) => void;
  onReplyError: (error: Error) => void;
}) {
  const translate = useTranslate();
  const messageId = useId();
  const form = useForm<FormValues>({
    defaultValues: {
      text: '',
    },
  });

  const dataProvider = useDataProvider<AdminDataProvider>();
  const sendMessage = useMutation({
    mutationFn: async (values: FormValues) => {
      onMessageSent(values);

      const response = await dataProvider.postUnitInsightLlmChatMessage({
        unitId,
        insightId,
        chatId,
        message: values.text,
        fileUrls: [],
      });

      let sentComplete = false;

      for await (const chunk of response) {
        if (chunk.type === 'error') {
          throw new Error(
            typeof chunk.error === 'string' ? chunk.error : 'Unknown error'
          );
        }

        if (chunk.data.type === 'chat-info') {
          onReplyStarted(chunk.data.data.chatId);
        } else if (chunk.data.type === 'chat-message-chunk') {
          onReplyChunk(chunk.data.data.text);
        } else {
          sentComplete = true;
          onReplyComplete({
            userMessageId: chunk.data.data.userMessageId,
            assistantMessageId: chunk.data.data.assistantMessageId,
          });

          form.reset();
        }
      }

      if (!sentComplete) {
        throw new Error('Chat response was not completed');
      }
    },
    onError: (error) => {
      onReplyError(error instanceof Error ? error : new Error('Unknown error'));
    },
  });

  return (
    <FormProvider {...form}>
      <form
        className="flex w-full flex-col rounded-md border bg-card"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(
          (data) => sendMessage.mutateAsync(data),
          (error) => console.error(error)
        )}
      >
        <label
          className="grid gap-2 p-3 text-sm font-medium"
          htmlFor={messageId}
        >
          {translate('moonstar.unitPerformance.chat.askQuestion')}
          <Textarea
            id={messageId}
            required
            className="min-h-32"
            {...form.register('text')}
          />
        </label>
        <div className="flex justify-end p-2">
          <Button
            variant="default"
            type="submit"
            disabled={form.formState.isSubmitting || sendMessage.isPending}
          >
            {form.formState.isSubmitting || sendMessage.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Send />
            )}
            {translate('moonstar.unitPerformance.chat.send')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
